import { EQuantityTypesLow } from "@/enums/quantity-types-low";
import type { LineItem } from "@/interfaces/estimate-detail";
import type { LineItemXactimateMapping } from "@/interfaces/payloads/xactimate";
import { FORMULA_SHORTHAND_VALUES, FORMULA_UNIT_VALUES, RoomMeasurementKey } from "@rebuild/shared";

const availableMeasurements = [
  "Floor(F)",
  "Ceiling(C)",
  "Walls(W)",
  "Wall and ceiling(WC)",
  "Perimeter of ceiling(PC)",
  "Perimeter of floor(PF)",
  "Long wall(LL)",
  "Short wall(SL)",
  "Volume of room(V)",
  "Floor below stairs(FBS)",
  "Walls below stairs(WBS)",
  "SF",
  "LF",
  "RL",
  "EA",
  "SQ",
  "P",
  "R",
  "Hip",
  "Val",
  "Hr",
  "DA",
  "GL",
  "WK",
  "CF",
  "MO",
];

const allUnits = [...FORMULA_UNIT_VALUES, ...FORMULA_SHORTHAND_VALUES];

const availableMeasurementsLow = availableMeasurements.map((m) => m.toLowerCase());

class EExtraDataMappingType {
  // errors - Make sure to add the type to the isError function when adding a new error type
  static readonly NoEmptyValueNoDefault = "no-empty-value-no-default";
  static readonly NotAllMappingsUsed = "not-all-mappings-used";
  static readonly InputWithNoMapping = "input-with-no-mapping";
  static readonly QuantityOutOfRange = "quantity-out-of-range";
  static readonly LowConfidenceInput = "low-confidence-input";
  static isError(type: string): boolean {
    return (
      type === EExtraDataMappingType.NoEmptyValueNoDefault ||
      type === EExtraDataMappingType.NotAllMappingsUsed ||
      type === EExtraDataMappingType.InputWithNoMapping ||
      type === EExtraDataMappingType.LowConfidenceInput ||
      type === EExtraDataMappingType.QuantityOutOfRange
    );
  }

  // modifers
  static readonly QuantityModifier = "quantity-modifier";
}

const roomMeasurementDisplayUnit: Record<RoomMeasurementKey, string> = {
  [RoomMeasurementKey.Walls]: "sf",
  [RoomMeasurementKey.Floors]: "sf",
  [RoomMeasurementKey.Ceilings]: "sf",
  [RoomMeasurementKey.FloorPerimeter]: "lf",
  [RoomMeasurementKey.CeilingPerimeter]: "lf",
};

export interface IQuantityFormula {
  initialValue: number;
  base: string;
  operator: string;
  modifierValue: number;
  modifierSuffix: string;
  extra?: string;
  isMultiUnit?: boolean;
  rawFormula?: string; // Store the original formula string for Xactimate
  /** "user" when a human typed/edited it — shown as entered, never folded by the display preference. */
  source?: "user" | "system";
}

export default class EngineSharedUtils {
  public static readonly prefixOrder: string[] = [
    "contents",
    "electrical",
    "plumbing",
    "walls_and_ceiling",
    "shelving",
    "doors_and_windows",
    "paint_and_stain",
    "flooring",
    "finish_trim",
    "final_cleaning",
    // type 1.1
    "bathroom_kitchen",
    "appliances",
    "general_hvac",
    "staircase",
    "kitchen_cabinetry",
    "plumbing",
    "laundry_room",
  ];

  static readonly formulaRegex =
    /^(?:(?<initial>(?:\d+(?:\.\d+)?|\d+\/\d+))(?:(?:\s*(?:[*x])\s*)|\s+))?(?<base>pc|pf|sf|lf|rl|w|c)(?:\s*(?<operator>[+-])\s*(?<modifier>\d+(?:\.\d+)?)(?<modifierSuffix>sf|lf|rl)?)?$/;

  static getFormulaString(formula: IQuantityFormula): string {
    // logDebug("formula", formula);
    if (formula.base.includes("flr_rpl")) {
      return "flr_rpl";
    }

    // Handle multi-unit formulas - return the raw formula as-is
    if (formula.isMultiUnit && formula.rawFormula) {
      return formula.rawFormula;
    }

    let base = formula.base;
    let showInitialValue = true;
    if (FORMULA_SHORTHAND_VALUES.includes(formula.base.toUpperCase())) {
      base = formula.base.toUpperCase();
      showInitialValue = formula.initialValue !== 1;
    }

    if (formula.operator) {
      return `${showInitialValue ? parseFloat(formula.initialValue.toFixed(2)) : ""}${base} ${formula.operator} ${parseFloat(
        formula.modifierValue.toFixed(2),
      )}${formula.modifierSuffix}`;
    } else {
      return `${showInitialValue ? parseFloat(formula.initialValue.toFixed(2)) : ""}${base}`;
    }
  }
  static readonly allowedPairs = [
    ["sf", "sf"],
    ["lf", "lf"],
    ["rl", "rl"],
    ["ea", "ea"],
    ["sq", "sq"],
    ["hr", "hr"],
    ["da", "da"],
    ["gl", "gl"],
    ["wk", "wk"],
    ["cf", "cf"],
    ["mo", "mo"],
    ["w", "sf"],
    ["wc", "sf"],
    ["flr_rpl", "sf"],
    ["c", "sf"],
    ["f", "sf"],
    ["pc", "lf"],
    ["pf", "lf"],
    ["pf", "sf"],
    ["p", "lf"],
    ["r", "lf"],
    ["hip", "lf"],
    ["val", "lf"],
  ];

  static removeAllSpaces(str: string) {
    return str.replace(/\s/g, "");
  }

  /**
   * Checks if a formula string contains multiple measurement units
   * Examples: W+C, C+4PF, 1/2F+PF, etc.
   */
  static isMultiUnitFormula(formulaString: string): boolean {
    const cleanFormula = formulaString.toLowerCase().trim();

    const unitPattern = new RegExp(`(${allUnits.join("|")})`, "g");
    const foundUnits = cleanFormula.match(unitPattern) || [];

    // Check if there are multiple units and at least one operator
    const hasOperator = /[+\-*/]/.test(cleanFormula);

    return foundUnits.length >= 2 && hasOperator;
  }

  /**
   * Helper function to categorize formula components
   */
  private static categorizeFormulaComponents(components: string[]): {
    shorthandMeasurements: string[];
    units: string[];
    invalid: string[];
  } {
    const shorthandMeasurements: string[] = [];
    const unitsFound: string[] = [];
    const invalid: string[] = [];

    for (const component of components) {
      if (FORMULA_SHORTHAND_VALUES.includes(component.toUpperCase())) {
        shorthandMeasurements.push(component);
      } else if (FORMULA_UNIT_VALUES.includes(component.toUpperCase())) {
        unitsFound.push(component);
      } else {
        invalid.push(component);
      }
    }

    return {
      shorthandMeasurements,
      units: unitsFound,
      invalid,
    };
  }

  /**
   * Validates multi-unit formula structure
   * Valid patterns:
   * - Multiple shorthand measurements: W+C, C+PF, 1/2F+PF
   * - One unit + shorthand measurements: NOT allowed multiple units
   */
  private static validateMultiUnitFormula(components: string[]): boolean {
    const categorized = this.categorizeFormulaComponents(components);

    // No invalid components allowed
    if (categorized.invalid.length > 0) {
      return false;
    }

    // Multiple units are NOT allowed (like SF+LF would be invalid)
    if (categorized.units.length > 1) {
      return false;
    }

    // Valid combinations:
    // 1. Only shorthand measurements (no units): W+C, C+PF, F+PF
    // 2. One unit + shorthand measurement

    return true;
  }

  /**
   * Parses multi-unit formulas like W+C, C+4PF, 1/2F+PF, etc.
   * Returns a formula object that preserves the original string for Xactimate
   *
   * Valid patterns:
   * - Multiple shorthand measurements: W+C, C+4PF, C+2PC, 1/2F+PF, W+F
   * - NOT multiple units: SF+LF would be invalid
   */
  static parseMultiUnitFormula(formulaString: string): IQuantityFormula | null {
    const cleanFormula = formulaString.toLowerCase().trim();

    // Extract all letter-based components (units/measurements)
    const componentMatches = cleanFormula.match(/([a-z]+)/g);
    if (!componentMatches) return null;

    // Validate the formula structure
    if (!this.validateMultiUnitFormula(componentMatches)) {
      return null;
    }

    // For multi-unit formulas, we'll use the first component as the base
    // and store the complete formula in rawFormula for Xactimate
    const firstComponent = componentMatches[0];

    return {
      initialValue: 1,
      base: firstComponent,
      operator: "",
      modifierValue: 0,
      modifierSuffix: "",
      isMultiUnit: true,
      rawFormula: formulaString.trim(),
    };
  }

  static getMeasurementTypeBase(base: string): string {
    const pair = EngineSharedUtils.allowedPairs.find((p) => p[0] === base);
    if (pair) {
      return pair[1];
    }
    return "";
  }
  static getAllowedUnitsFromXactimateMapping(lineItem: LineItem, xactimateMapping: LineItemXactimateMapping[]): string[] {
    const mappings = xactimateMapping.filter(
      (m) =>
        m.lineItemId === lineItem.lineItemId &&
        this.removeAllSpaces(m.lineItem).toLowerCase() === this.removeAllSpaces(lineItem.lineItem).toLowerCase(),
    );

    if (mappings.length > 0) {
      const uniqueUnits = Array.from(
        mappings.reduce((uniqueSet, mapping) => {
          if (mapping && typeof mapping.quantity === "string" && mapping.quantity.trim() !== "") {
            uniqueSet.add(mapping.quantity.toLowerCase());
          }
          return uniqueSet;
        }, new Set<string>()),
      );
      return uniqueUnits;
    }
    return [];
  }

  static checkAndFixFormula(formula: IQuantityFormula, defualtMeasurement: string, allowedUnits?: string[]): string {
    // logDebug("formulaTest", formula);

    // For multi-unit formulas, skip most validation and pass through to Xactimate
    if (formula.isMultiUnit) {
      // Just ensure it has a valid rawFormula
      if (!formula.rawFormula || formula.rawFormula.trim().length === 0) {
        return "Invalid multi-unit formula";
      }
    }

    defualtMeasurement = defualtMeasurement.toLowerCase();
    formula.base = formula.base.toLowerCase();
    formula.modifierSuffix = formula.modifierSuffix?.toLowerCase();

    if (formula.initialValue === 0) return "Initial value cannot be zero";
    if (formula.extra && formula.extra.trim().length > 0) return `Extra unparsed content "${formula.extra}" in formula`;
    if (!formula.base) {
      formula.base = defualtMeasurement;
      if (allowedUnits?.length) {
        formula.base = allowedUnits[0];
      }
    }
    if (formula.base && formula.modifierSuffix) {
      // check if in allowed pairs
      const pair = EngineSharedUtils.allowedPairs.find((p) => p[0] === formula.base && p[1] === formula.modifierSuffix);
      if (!pair) return `Invalid formula "${formula.base} and ${formula.modifierSuffix}"`;
    }
    if (!formula.modifierSuffix && formula.operator !== "/" && formula.operator !== "*" && formula.operator !== "x") {
      if (!EngineSharedUtils.allowedPairs.find((p) => p[0] === formula.base)?.length) {
        return `Invalid formula`;
      }
    }
    if (formula.operator) {
      if (!formula.modifierValue) {
        return `+ or - found but no modifier number in "${formula}"`;
      }
    }
    if (allowedUnits?.length) {
      const validQuantity = EngineSharedUtils.allowedPairs.find((p) => p[0] === formula.base && allowedUnits.includes(p[1]))?.[1];
      if (!validQuantity) {
        return `Invalid units, please use ${allowedUnits.map((q) => `"${q.toUpperCase()}"`).join(", ")}`;
      }

      formula.modifierSuffix = validQuantity;
    }

    // logDebug("formulaTestEnd", formula);
    return "";
  }

  static extractFormulaFromString(formulaString: string): IQuantityFormula | null {
    // logDebug("~~~~~formula", formulaString);
    const originalFormulaString = formulaString.trim();
    formulaString = formulaString.toLowerCase().trim();

    // Check if this is a multi-unit formula first
    if (this.isMultiUnitFormula(formulaString)) {
      return this.parseMultiUnitFormula(originalFormulaString);
    }

    let remaining = formulaString;
    let initial: string | undefined,
      base: string | undefined,
      operator: string | undefined,
      modifier: string | undefined,
      initialValue: number | undefined,
      modifierValue: number | undefined,
      modifierSuffix: string | undefined;
    initialValue = 1;
    modifierValue = 0;
    operator = "";
    modifierSuffix = "";

    if (formulaString.includes("flr_rpl")) {
      return {
        initialValue: 1,
        base: "flr_rpl",
        operator: "",
        modifierValue: 0,
        modifierSuffix: "",
      };
    }

    // Extract initial number (int, float, or fraction)
    let m = remaining.match(/^((?:\d+\/\d+)|(?:\d+(?:\.\d+)?))\s*/);
    if (m) {
      initial = m[1];
      remaining = remaining.slice(m[0].length);
      if (initial.includes("/")) {
        const [num, den] = initial.split("/");
        const numerator = parseFloat(num);
        const denominator = parseFloat(den);
        if (denominator !== 0) initialValue = numerator / denominator;
        else {
          // logError(`Division by zero in fraction "${initial}"`);
          return null;
        }
      } else {
        initialValue = parseFloat(initial);
      }
      // Remove multiplier if present (* or x with optional spaces)
      m = remaining.match(/^(?:[*x])\s*/i);
      if (m) remaining = remaining.slice(m[0].length);
    }
    // logDebug("initial", initialValue);
    // logDebug("remaining", remaining);

    // Extract base unit (pc, pf, sf, lf, rl, w, or c, wc)
    m = remaining.match(/^(pc|pf|cf|wk|hr|wc|w|c|f|sf|lf|rl|ea|sq|da|gl|mo|hip|val|p|r)\s*/i);
    if (m) {
      base = m[1];
      remaining = remaining.slice(m[0].length);
    } else {
      // this is just a number situation
      return { initialValue, base: "", operator: "", modifierValue: 0, modifierSuffix: "" };
    }

    // logDebug("base", base);
    // logDebug("remaining", remaining);
    // Extract optional operator (+ or -) and modifier number (int or float)
    m = remaining.match(/^([+-/*x])\s*/);
    if (m) {
      operator = m[1];
      remaining = remaining.slice(m[0].length);
      // logDebug("operator", operator);
      // logDebug("remaining", remaining);
      m = remaining.match(/^(\d+(?:\.\d+)?)/);

      if (m) {
        modifier = m[1];
        remaining = remaining.slice(m[0].length);
        modifierValue = parseFloat(modifier);
        // logDebug("modifier", modifierValue);
        // logDebug("remaining", remaining);
      } else {
        // logError(`Operator found but no modifier number in "${formulaString}"`);
        return null;
      }
      // logDebug("testing", remaining, "for sf");
      // Optional modifier suffix (sf, lf, or rl)
      m = remaining.match(/^\s*(sf|lf|rl|ea)\s*/i);
      if (m) {
        modifierSuffix = m[1];
        remaining = remaining.slice(m[0].length);
        // logDebug("modifierSuffix", modifierSuffix);
        // logDebug("remaining", remaining);
      }
    }
    // logDebug("last remaining", remaining);
    if (remaining.trim().length > 0) {
      // logWarning(`Extra unparsed content "${remaining}" in formula "${formulaString}"`);
    }
    // logDebug("returning", { initialValue, base, operator, modifierValue, modifierSuffix, extra: remaining });
    return { initialValue, base, operator, modifierValue, modifierSuffix, extra: remaining };
  }

  static getQuantityStringForLineItem(lineItem: LineItem, errorQuantity = "na"): string {
    // lineItem.lineItem.includes("hung") && logDebug("lineItem", lineItem);
    if (lineItem.formula) {
      return this.getFormulaString(lineItem.formula);
    } else if (lineItem.qnm?.length) {
      if (lineItem.qnm[0].measurement === "flr_rpl") {
        return "flr_rpl";
      }
      return `${lineItem.qnm[0].quantity} ${lineItem.qnm[0].measurement}`;
    }
    return errorQuantity;
  }
  private static _getFinalQuantityForFormula(
    lineItem: LineItem,
    pageId: string,
    quantityBeforeModifierFunc: (id: string, measurementType: string) => number,
  ): string {
    // if (lineItem.lineItem === "walls seal with pva primer 1 coat" && lineItem.formula?.base === "wc") {
    // 	noop();
    // }
    // we support on these here
    const measurementToCellnameMap: { [key: string]: string } = {
      f: RoomMeasurementKey.Floors,
      c: RoomMeasurementKey.Ceilings,
      w: RoomMeasurementKey.Walls,
      // wc: RoomMeasurementKey.Walls, // TODO ::
      pc: RoomMeasurementKey.CeilingPerimeter,
      pf: RoomMeasurementKey.FloorPerimeter,
    };
    const formula = lineItem.formula!;
    if (formula.base === "flr_rpl") {
      const floorMeasurments = quantityBeforeModifierFunc(pageId, measurementToCellnameMap["f"]);
      return `${Math.ceil(1.15 * floorMeasurments)} sf`;
    }
    if (!formula.initialValue || formula.initialValue <= 0) {
      return "na";
    }
    let quantityBeforeModifier = 1; //quantityBeforeModifierFunc(pageId, measurementToCellnameMap[formula.base]);
    if (measurementToCellnameMap[formula.base] !== undefined) {
      // must be f, c, w, pc, pf
      quantityBeforeModifier = quantityBeforeModifierFunc(pageId, measurementToCellnameMap[formula.base]); //return EngineSharedUtils.getFormulaString(formula);
    }
    if (!formula.operator) {
      return `${parseFloat((formula.initialValue * quantityBeforeModifier).toFixed(2))} ${formula.modifierSuffix}`;
    } else {
      if (formula.operator === "+") {
        return `${parseFloat((formula.initialValue * quantityBeforeModifier + formula.modifierValue).toFixed(2))} ${
          formula.modifierSuffix
        }`;
      } else if (formula.operator === "-") {
        return `${parseFloat((formula.initialValue * quantityBeforeModifier - formula.modifierValue).toFixed(2))} ${
          formula.modifierSuffix
        }`;
      } else if (formula.operator === "*" || formula.operator === "x") {
        return `${parseFloat((formula.initialValue * quantityBeforeModifier * formula.modifierValue).toFixed(2))} ${
          formula.modifierSuffix
        }`;
      } else if (formula.operator === "/") {
        return `${parseFloat(((formula.initialValue * quantityBeforeModifier) / formula.modifierValue).toFixed(2))} ${
          formula.modifierSuffix
        }`;
      }
    }
    return "na";
  }
  static getFinalQuantity(
    lineItem: LineItem,
    pageId: string,
    quantityBeforeModifierFunc: (id: string, measurementType: string) => number,
  ): string {
    if (lineItem.formula) {
      return this._getFinalQuantityForFormula(lineItem, pageId, quantityBeforeModifierFunc);
    }
    // this is here for backward compatibility
    // with time we shouldn't have anymore line items without formula
    const measurementToCellnameMap: { [key: string]: string } = {
      "walls(w)": RoomMeasurementKey.Walls,
      "ceiling(c)": RoomMeasurementKey.Ceilings,
      "floor(f)": RoomMeasurementKey.Floors,
      "perimeter of floor(pf)": RoomMeasurementKey.FloorPerimeter,
      "perimeter of ceiling(pc)": RoomMeasurementKey.CeilingPerimeter,
      f: RoomMeasurementKey.Floors,
      c: RoomMeasurementKey.Ceilings,
      w: RoomMeasurementKey.Walls,
      // wc: RoomMeasurementKey.Walls, // TODO ::
      pc: RoomMeasurementKey.CeilingPerimeter,
      pf: RoomMeasurementKey.FloorPerimeter,
      floor: RoomMeasurementKey.Floors,
      ceiling: RoomMeasurementKey.Ceilings,
      walls: RoomMeasurementKey.Walls,
      wall: RoomMeasurementKey.Walls,
      floors: RoomMeasurementKey.Floors,
    };
    // if (tuple.tupleId === 'electrical_baseboard_electric_heater') {
    // 	noop();
    // }
    const measurement = lineItem.qnm.length ? lineItem.qnm[0].measurement : "";
    if (measurement && measurement.includes("flr_rpl")) {
      const floorMeasurments = quantityBeforeModifierFunc(pageId, measurementToCellnameMap["f"]);
      return `${Math.ceil(1.15 * floorMeasurments)} sf`;
    }
    // logDebug('measurement', measurement);
    const quantity = (lineItem.qnm.length && lineItem.qnm[0].quantity) || 1;
    if (!measurement) {
      return "na";
    }
    function getMeasurement(measurement: string) {
      const cellName = measurementToCellnameMap[measurement] as RoomMeasurementKey | undefined;
      return cellName ? roomMeasurementDisplayUnit[cellName] : measurement;
    }
    if (availableMeasurementsLow.includes(measurement) || Object.keys(measurementToCellnameMap).includes(measurement)) {
      const quantityBeforeModifier = quantityBeforeModifierFunc(pageId, measurementToCellnameMap[measurement]);
      const onlyModifiers = lineItem?.mappingExtraData?.filter(
        (m) =>
          m.type === EExtraDataMappingType.QuantityModifier &&
          m.mappings![0].mapping?.quantityType !== EQuantityTypesLow.QuantityAsValue &&
          m.mappings![0].mapping?.quantityType !== EQuantityTypesLow.NoQuantity,
      );
      let quantityType: string | undefined;
      let quantityValue: number = 1;

      // TODO :: we need to take care of the substract case
      if (onlyModifiers?.length === 0 && Object.keys(measurementToCellnameMap).includes(measurement)) {
        if (quantity < 0) {
          quantityType = EQuantityTypesLow.QuantityAsSubtract;
          quantityValue = quantity;
        } else {
          quantityType = EQuantityTypesLow.QuantityAsMultiplier;
          quantityValue = quantity;
        }
      }
      if (onlyModifiers?.length) {
        quantityType = onlyModifiers[0].mappings![0].mapping!.quantityType;
        quantityValue = parseFloat(onlyModifiers[0].mappings![0].mapping!.quantityValue || "1");
      }
      if (onlyModifiers?.length || Object.keys(measurementToCellnameMap).includes(measurement)) {
        if (quantityType === EQuantityTypesLow.QuantityAsMultiplier) {
          return `${quantityValue * (quantityBeforeModifier || quantity)} ${getMeasurement(measurement)}`;
        } else if (quantityType === EQuantityTypesLow.QuantityAsSubtract) {
          return `${(quantityBeforeModifier || quantity) + quantityValue} ${getMeasurement(measurement)}`; // quantityValue is negative
        }
      } else {
        const q = quantityBeforeModifierFunc(pageId, measurementToCellnameMap[measurement]);
        return q ? `${q} ${getMeasurement(measurement)}` : `${quantity} ${measurement}`;
      }
    }
    return "na1";
  }

  static fuzzyMatch(str: string) {
    const map = {
      0: ["0", "o", "O"],
      1: ["1", "l", "i", "I", "j", "|"],
      2: ["2", "z", "Z"],
      4: ["4", "A"],
      5: ["5", "S", "s"],
      6: ["6", "b"],
      7: ["7", "T"],
      8: ["8", "B"],
      9: ["9", "g", "q"],
    };
    const pass0 = str
      .split("")
      .map((c) => {
        const found = Object.entries(map).find(([, value]) => value.includes(c));
        return found ? found[0] : c;
      })
      .join("");
    // now change consecutive duplicates to single character
    const pass1 = pass0.replace(/(.)\1+/g, "$1");
    return pass1;
  }
}
